import React, { useContext, useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { ADMIN_TABS, OPERATOR_TABS, PAGES_BY_KEY, pageKeyForPath } from "../pages";
import { readQueueStation } from "../queueStation";
import { useMobileMode } from "../MobileModeContext.jsx";
import { AdminContext } from "../../adminSession.jsx";
import useOnline from "../useOnline";
import MorePanel from "./MorePanel.jsx";
import "../mobile.css";

// The phone layout (docs/mobile-pwa-plan.md section 4): a top bar (title, back, station /
// admin), the page, and bottom tabs. Used only on pages switched on for phones
// (mobile/mobileMode.js); every other page keeps today's layout. Layout only: data and actions
// come from shared modules.


function isDevSite() {
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1" || h.startsWith("dev.");
}

function tabTarget(key, activePages) {
  if (key === "home") return activePages.has("dashboard") ? "/dashboard" : "/";
  return PAGES_BY_KEY[key] ? PAGES_BY_KEY[key].path : "/";
}

function TabIcon({ icon }) {
  return <i className={`fas ${icon}`} aria-hidden="true" />;
}

export default function PhoneShell({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const mode = useMobileMode();
  const admin = useContext(AdminContext);
  const online = useOnline();
  const [moreOpen, setMoreOpen] = useState(false);
  const [station, setStation] = useState(readQueueStation);

  const isAdmin = Boolean(admin && admin.isAdminActive);
  const key = pageKeyForPath(location.pathname);
  const page = key ? PAGES_BY_KEY[key] : null;
  const atRoot = location.pathname === "/";
  const tabs = isAdmin ? ADMIN_TABS : OPERATOR_TABS;

  // Room for the bottom tabs, for anything fixed at the bottom of the screen (update banner).
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("jq-phone-active");
    return () => root.classList.remove("jq-phone-active");
  }, []);

  useEffect(() => {
    setStation(readQueueStation());
    setMoreOpen(false);
  }, [location.pathname]);

  const title = atRoot ? "JobQ" : (page ? page.label : "JobQ");

  return (
    <div className="jq-phone" data-testid="phone-shell">
      <header className="jq-topbar">
        <div className="jq-topbar-left">
          {!atRoot && (
            <button
              type="button"
              className="jq-icon-btn"
              aria-label="Back"
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
            >
              <i className="fas fa-chevron-left" aria-hidden="true" />
            </button>
          )}
          <h1 className="jq-topbar-title">{title}</h1>
          {isDevSite() && <span className="jq-chip jq-chip-dev">DEV</span>}
          {mode.prefs.preview && !mode.mobileFeature.enabled && <span className="jq-chip">Preview</span>}
        </div>
        <div className="jq-topbar-right">
          {station && (
            <span className="jq-chip" title="Station">
              <i className="fas fa-industry me-1" aria-hidden="true" />{station}
            </span>
          )}
          <button
            type="button"
            className={`jq-chip jq-chip-button ${isAdmin ? "jq-chip-admin" : ""}`}
            onClick={() => setMoreOpen(true)}
            aria-label={isAdmin ? `Admin unlocked: ${admin.adminData.userName}` : "Admin locked"}
          >
            <i className={`fas ${isAdmin ? "fa-unlock" : "fa-lock"}`} aria-hidden="true" />
            {isAdmin && <span className="ms-1">{String(admin.adminData.userName || "").split(" ")[0]}</span>}
          </button>
        </div>
      </header>

      {!online && (
        <div className="jq-offline" role="status">
          <i className="fas fa-wifi me-2" aria-hidden="true" />
          Offline. Nothing can be saved until the phone is back online.
        </div>
      )}

      <main className="jq-phone-main">{children}</main>

      <nav className="jq-tabbar" aria-label="Main">
        {tabs.map((t) => {
          if (t === "more") {
            return (
              <button key="more" type="button" className={`jq-tab ${moreOpen ? "active" : ""}`} onClick={() => setMoreOpen(true)}>
                <TabIcon icon="fa-bars" />
                <span>More</span>
              </button>
            );
          }
          const p = PAGES_BY_KEY[t];
          const to = tabTarget(t, mode.activePages);
          const label = t === "sales-orders" ? "Orders" : t === "queue" ? "Queue" : p.label;
          return (
            <NavLink
              key={t}
              to={to}
              end={to === "/"}
              className={({ isActive }) => `jq-tab ${isActive ? "active" : ""}`}
            >
              <TabIcon icon={p.icon} />
              <span>{label}</span>
            </NavLink>
          );
        })}
      </nav>

      <MorePanel open={moreOpen} onClose={() => setMoreOpen(false)} />
    </div>
  );
}
